import { useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackDisabledSelectAll } from '@air/analytics';
import {
  hasSelectedItemsSelector,
  resetSelectedItemsAction,
  selectedItemsCountSelector,
  setShowShiftSelectHighlightsAction,
  showShiftSelectHighlightsSelector,
} from '@air/redux-selected-items';

import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import useIsShiftPressed from '~/hooks/useIsShiftPressed';
import usePrevious from '~/hooks/usePrevious';
import { getClipIdFromPath } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

let _stopMultiselectEscKeyPropagation = false;

export const stopMultiselectEscKeyPropagation = (nextValue: boolean) => {
  _stopMultiselectEscKeyPropagation = nextValue;
};

/**
 * This component enables hotkeys for select feature:
 * - shift for multiselect
 * - esc to clear selection
 *
 * Note: Cmd+A/Ctrl+A select all is handled by SelectAllKeys in private board views.
 */
export const MultiselectKeys = () => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackDisabledSelectAll } = useTrackDisabledSelectAll();
  const holdingShift = useIsShiftPressed();
  const hasSelectedItems = useSelector(hasSelectedItemsSelector);
  const isShowingMultiselectHighlight = useSelector(showShiftSelectHighlightsSelector);
  const previousIsShowingMultiselectHighlight = usePrevious(isShowingMultiselectHighlight);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);

  // this effect determines whether to turn on showing selected clip previews
  useEffect(() => {
    const showShiftSelectHighlights = holdingShift && hasSelectedItems;

    if (showShiftSelectHighlights !== previousIsShowingMultiselectHighlight) {
      dispatch(setShowShiftSelectHighlightsAction({ showShiftSelectHighlights }));
    }
  }, [dispatch, holdingShift, hasSelectedItems, previousIsShowingMultiselectHighlight]);

  useHotkeys(
    'esc',
    () => {
      // @TODO(COR-1645): Update this to properly disable the shortcut instead of an early return
      const clipIdInUrl = getClipIdFromPath(window.location.pathname);

      // Don't deselect items if using escape key to close an asset
      if (!clipIdInUrl) {
        if (selectAll) {
          trackDisabledSelectAll({
            trigger: 'escape',
            totalSelectionCount: selectedItemsCountSelector(store.getState()),
          });
          resetSelectAll();
        }
        dispatch(resetSelectedItemsAction());
      }

      // After resetting selected items, data-focus-visible-added is present on the last selected element
      // We need to clear it manually
      const focused = document.querySelectorAll('[data-focus-visible-added]');

      if (focused && focused.length > 0) {
        // @ts-expect-error
        focused[0].blur();
      }
    },
    {
      enabled: !_stopMultiselectEscKeyPropagation,
    },
    [selectAll, resetSelectAll, dispatch, store, trackDisabledSelectAll],
  );

  return null;
};
